/**
 * Diamond Detail View (ddv-)
 *
 * Sits between the Aurexel Diamond Selector app's "Select Stone" button and the
 * app's own selection. The app's code is not changed:
 *  - a capture-phase click listener stops "Select Stone" before the app sees it
 *    and opens this overlay instead;
 *  - "Finish Your Setting" clicks the same app button again with the listener
 *    switched off, so the app selects the stone, shows its Selected Diamond card
 *    and enables Add to Cart exactly as before (same cart payload).
 * If anything here fails, the click is let through and the app behaves as before.
 */
(function () {
  'use strict';
  
  // The theme editor re-renders sections in place, so everything below runs in boot()
  // and is torn down (listeners removed, hidden sections restored) before re-running.
  var teardown = null;
  function start() {
    if (teardown) teardown();
    teardown = boot();
  }
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', function () { if (teardown) teardown(); teardown = null; });
  start();

  function boot() {
  var bound = [];
  function on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    bound.push([target, type, fn, opts]);
  }

  var root = document.querySelector('[data-ddv-root]');
  if (!root) return null;

  // Selectors owned by the Aurexel app (see its diamond-selector.js).
  var APP_AREA = '#diamond-selector-widget, #ds-modal-root-wrapper';
  var APP_SELECT_BTN = '[data-action-select]';
  var APP_VIEW_BTN = '#ds-sp-view-btn';
  var APP_CHANGE_BTN = '#ds-sp-change-btn';

  var DEFAULT_GRADES = {
    'COLOR:J': ['Near colorless', 'A faint warm tint that is hard to see once set, especially in yellow or rose gold.'],
    'COLOR:I': ['Near colorless', 'A slight warmth visible only when compared side by side with whiter stones.'],
    'COLOR:H': ['Near colorless', 'Appears white to the eye; an excellent balance of beauty and value.'],
    'COLOR:G': ['Near colorless', 'Very white; any hint of color is practically invisible once set.'],
    'COLOR:F': ['Colorless', 'Colorless to the naked eye; only an expert can find a trace of color.'],
    'COLOR:E': ['Colorless', 'Exceptionally white; minute traces detectable only by a gemologist.'],
    'COLOR:D': ['Colorless', 'The highest color grade: absolutely colorless and icy white.'],
    'CLARITY:SI1': ['Slightly included', 'Inclusions visible under 10x magnification, usually not visible to the eye.'],
    'CLARITY:VS2': ['Very slightly included', 'Minor inclusions that are difficult to see under 10x magnification.'],
    'CLARITY:VS1': ['Very slightly included', 'Tiny inclusions, very difficult to see even under 10x magnification.'],
    'CLARITY:VVS2': ['Very, very slightly included', 'Minute inclusions that are very difficult for an expert to see at 10x.'],
    'CLARITY:VVS1': ['Very, very slightly included', 'Minute inclusions that are extremely difficult for an expert to see at 10x.'],
    'CLARITY:IF': ['Internally flawless', 'No internal inclusions under 10x magnification; only tiny surface marks.'],
    'CLARITY:FL': ['Flawless', 'No inclusions or blemishes visible under 10x magnification.'],
    'CUT:GOOD': ['Good', 'Reflects most of the light that enters it, offering good sparkle for the value.'],
    'CUT:VERY GOOD': ['Very Good', 'Reflects nearly as much light as an Excellent cut, with strong brilliance.'],
    'CUT:EXCELLENT': ['Excellent', 'The finest proportions and finish for maximum brilliance and fire.']
  };

  var config = {};
  try { config = JSON.parse(root.querySelector('[data-ddv-config]').textContent); } catch (e) { config = {}; }
  config.ring = config.ring || { variants: [] };
  config.labels = config.labels || {};
  config.grades = config.grades || {};
  config.shapes = config.shapes || {};
  config.cgStones = config.cgStones || {};

  var overlay = root.querySelector('[data-ddv-overlay]');
  var pagebar = document.querySelector('[data-ddv-pagebar]');
  var state = { confirmed: null, open: false, mode: 'select', id: null, diamond: null, originBtn: null, lastFocus: null, bypass: false, req: 0 };

  /* ---------- helpers ---------- */
  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function has(v) { return v != null && String(v).trim() !== '' && String(v).trim() !== '—'; }
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : null; }

  function activeCurrency() {
    return ((window.Shopify && Shopify.currency && Shopify.currency.active) || root.dataset.currency || '').toUpperCase();
  }

  // Same currency rules as the app (its window.DiamondCurrency helper), so the
  // diamond price and total here match the app's card and Add to Cart label.
  function currencyMode() {
    var product = window.__DIAMOND_SELECTOR_PRODUCT__ || {};
    var opts = {
      shopperCurrency: activeCurrency(),
      storeCurrency: product.shopCurrency || activeCurrency(),
      rate: window.Shopify && Shopify.currency && Shopify.currency.rate
    };
    if (window.DiamondCurrency) return window.DiamondCurrency.displayMode(opts);
    return { currency: opts.shopperCurrency, shopperCurrency: opts.shopperCurrency, storeCurrency: opts.storeCurrency, factor: 1 };
  }

  // Formats like the store's money format (e.g. "Rs. 1,234.56") when the amount is
  // in the shopper's currency; otherwise falls back to the browser's formatter.
  function money(amount, currency) {
    if (amount == null || !isFinite(amount)) return '';
    currency = currency || activeCurrency();
    var sample = (window.__DIAMOND_SELECTOR_PRODUCT__ || {}).moneySample;
    var m = sample && sample.match(/1(\D?)234\1(?:567)(?:(\D)89)?/);
    if (m && currency === activeCurrency()) {
      var decimals = m[2] ? 2 : 0;
      var parts = Math.abs(amount).toFixed(decimals).split('.');
      var text = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, m[1] || '') + (parts[1] ? m[2] + parts[1] : '');
      return (amount < 0 ? '-' : '') + sample.replace(m[0], text);
    }
    try {
      return new Intl.NumberFormat(root.dataset.locale || document.documentElement.lang || undefined, {
        style: 'currency', currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  // Fields live in the overlay and in the product-page step bar section.
  function fields(name) { return document.querySelectorAll('[data-ddv-root] [data-ddv-field="' + name + '"], [data-ddv-pagebar] [data-ddv-field="' + name + '"]'); }
  function setText(name, text) { fields(name).forEach(function (el) { el.textContent = text || ''; }); }
  function setHTML(name, html) { fields(name).forEach(function (el) { el.innerHTML = html || ''; }); }

  /* ---------- ring (current variant) ---------- */
  function mainCartForm() {
    var forms = Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"]'));
    return forms.filter(function (f) { return !f.closest(APP_AREA) && f.querySelector('[name="id"]'); })[0] || null;
  }
  function ringVariant() {
    var form = mainCartForm();
    var id = form && form.querySelector('[name="id"]') ? Number(form.querySelector('[name="id"]').value) : config.ring.variantId;
    var list = config.ring.variants || [];
    return list.filter(function (v) { return v.id === id; })[0] || list.filter(function (v) { return v.id === config.ring.variantId; })[0] || null;
  }
  function ringPrice() { var v = ringVariant(); return v ? v.price / 100 : null; }

  /* ---------- diamond data ---------- */
  function normalize(d) {
    var m = String(d.measurement || d.measurements || '').replace(/mm/i, '').split(/\s*[x×*]\s*/i).map(num).filter(function (n) { return n != null; });
    var mode = currencyMode();
    var fx = d.fx || {};
    var amount = null;
    if (d.displayPrice != null && String(fx.currency || '').toUpperCase() === mode.storeCurrency) {
      amount = window.DiamondCurrency ? window.DiamondCurrency.toDisplay(d.displayPrice, mode) : num(d.displayPrice);
    } else if (d.__scrapedPrice != null) {
      amount = d.__scrapedPrice;
    }
    if (amount != null && amount < 1) amount = null;
    var currency = mode.currency;
    var lab = String(d.origin || d.growthType || '').toLowerCase();
    return {
      id: String(d.id),
      shape: d.shape || '',
      carat: num(d.carat),
      color: d.color || '',
      clarity: d.clarity || '',
      cut: d.cut || '',
      ratio: d.lwRatio || (m.length >= 2 && m[1] ? (m[0] / m[1]).toFixed(2) : ''),
      length: m[0] || null,
      width: m[1] || null,
      depthMm: m[2] || null,
      table: d.tablePercent || '',
      depth: d.depthPercent || '',
      fluor: d.fluorIntensity || '',
      polish: d.polishLong || d.polish || '',
      symmetry: d.symmetryLong || d.symmetry || '',
      lab: d.lab || '',
      reportNo: d.reportNo || '',
      certUrl: d.certificateUrl || d.certUrl || '',
      isLab: !lab || /lab|cvd|hpht/.test(lab),
      price: amount,
      currency: currency,
      images: [d.imageUrl || d.image, d.fallbackImageUrl].filter(Boolean),
      video: d.videoUrl || ''
    };
  }

  function fetchDiamond(id) {
    var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var url = base + 'apps/diamonds/' + encodeURIComponent(id) + '?currency=' + encodeURIComponent(currencyMode().storeCurrency);
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  // Fallback when the app endpoint fails: read what the app already shows in the open row.
  function scrapeFromButton(btn, id) {
    var card = btn && btn.closest('.ds-expanded-simple-card, .ds-diamond-modal-card');
    var d = { id: id };
    if (!card) return d;
    var title = card.querySelector('.ds-simple-specs-title, .ds-modal-main-title');
    if (title) {
      var t = title.textContent;
      var c = t.match(/([\d.]+)\s*ct/i); if (c) d.carat = c[1];
      var parts = t.split('·').map(function (s) { return s.trim(); });
      if (parts[1]) d.shape = parts[1];
    }
    card.querySelectorAll('.ds-simple-spec-line').forEach(function (line) {
      var l = (line.querySelector('.ds-spec-label') || {}).textContent || '';
      var v = line.querySelector('.ds-spec-value, .ds-cert-link');
      v = v ? v.textContent.trim() : '';
      if (/certificate/i.test(l)) d.reportNo = v;
      else if (/color/i.test(l)) d.color = v;
      else if (/clarity/i.test(l)) d.clarity = v;
      else if (/measure/i.test(l)) d.measurement = v;
      else if (/ratio/i.test(l)) d.lwRatio = v;
    });
    var price = card.querySelector('.ds-simple-price-val');
    if (price) {
      var digits = price.textContent.replace(/[^\d.,]/g, '').replace(/,/g, '');
      if (digits) d.__scrapedPrice = num(digits);
    }
    var img = card.querySelector('img');
    if (img) d.imageUrl = img.currentSrc || img.src;
    var vid = card.querySelector('[data-video]');
    var frame = card.querySelector('iframe[src]');
    var video = card.querySelector('video[src]');
    d.videoUrl = (vid && vid.getAttribute('data-video')) || (frame && frame.getAttribute('src')) || (video && video.getAttribute('src')) || '';
    return d;
  }

  /* ---------- rendering ---------- */
  function title(d) {
    return [d.carat != null ? d.carat.toFixed(2) + ' Carat' : '', d.color, d.clarity, d.shape, d.isLab ? 'Lab Diamond' : 'Diamond']
      .filter(Boolean).join(' ');
  }
  function shortTitle(d) {
    return [d.carat != null ? d.carat.toFixed(2) + ' ct' : '', d.shape, d.color, d.clarity].filter(Boolean).join(' ');
  }

  // Product-page step bar: shows the stone confirmed with "Finish Your Setting".
  function renderPageBar() {
    if (!pagebar) return;
    var d = state.open && state.diamond ? state.diamond : state.confirmed;
    var done = !!state.confirmed && !state.open;
    var ring = ringPrice();
    var cur = activeCurrency();
    pagebar.querySelectorAll('[data-ddv-stone-filled]').forEach(function (el) { el.hidden = !d; });
    pagebar.querySelectorAll('[data-ddv-stone-empty]').forEach(function (el) { el.hidden = !!d; });
    var stone = pagebar.querySelector('[data-step="stone"]');
    var ringStep = pagebar.querySelector('[data-step="ring"]');
    stone.querySelector('.ddv-check').hidden = !done;
    stone.classList.toggle('ddv-step--done', done);
    stone.classList.toggle('ddv-step--active', !done);
    ringStep.classList.toggle('ddv-step--active', done);
    if (done) { stone.removeAttribute('aria-current'); ringStep.setAttribute('aria-current', 'step'); }
    else { stone.setAttribute('aria-current', 'step'); ringStep.removeAttribute('aria-current'); }
    setText('pageStoneTitle', d ? shortTitle(d) : '');
    setText('pageStonePrice', d ? money(d.price, d.currency) : '');
    setHTML('pageStoneThumb', d && d.images[0] ? '<img src="' + esc(d.images[0]) + '" alt="" loading="lazy">' : '');
    setText('pageTotal', d && d.price != null && ring != null && d.currency === cur ? money(ring + d.price, cur) : money(ring, cur));
  }

  // Final step (stone confirmed, view closed): a clean Setting / Diamond / Total summary
  // above Add to Cart; CSS under html.ddv-complete hides what is no longer needed.
  function renderComplete() {
    var done = !!state.confirmed && !state.open;
    var html = document.documentElement;
    html.classList.toggle('ddv-complete', done);
    var form = mainCartForm();
    // Keep the ring size picker visible until a size is chosen (it is required).
    var size = document.querySelector('.rsp [data-rsp-property]');
    html.classList.toggle('ddv-size-chosen', !!(size && size.value));
    var box = document.querySelector('[data-ddv-summary]');
    if (!done || !form) { if (box) box.hidden = true; return; }
    if (!box) {
      box = document.createElement('div');
      box.className = 'ddv-summary';
      box.setAttribute('data-ddv-summary', '');
      form.parentNode.insertBefore(box, form);
    }
    var d = state.confirmed;
    var ring = ringPrice();
    var cur = activeCurrency();
    var total = d.price != null && ring != null && d.currency === cur ? money(ring + d.price, cur) : '';
    var ICON_SETTING = '<svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="24" cy="31" r="10" stroke-width="1.15"/><circle cx="24" cy="31" r="8.3" stroke-width="0.8"/><path d="M21.6 21.1L20.4 17.6h7.2l-1.2 3.5" stroke-width="1"/><path d="M24 7.2c.3 1.9.8 2.4 2.7 2.7-1.9.3-2.4.8-2.7 2.7-.3-1.9-.8-2.4-2.7-2.7 1.9-.3 2.4-.8 2.7-2.7z" stroke-width="0.8"/></g></svg>';
    var ICON_DIAMOND = '<svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true"><path d="M14 10h20l8 10-18 20L6 20zM6 20h36M18 10l-4 10 10 20M30 10l4 10-10 20M14 20l10-10 10 10" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>';
    function row(icon, label, name, price, extra) {
      return '<div class="ddv-summary__row"><span class="ddv-summary__icon">' + icon + '</span>' +
        '<span class="ddv-summary__text"><span class="ddv-summary__label">' + esc(label) + '</span>' +
        '<span class="ddv-summary__name">' + esc(name) + '</span>' + (extra || '') + '</span>' +
        '<span class="ddv-summary__price">' + esc(price) + '</span></div>';
    }
    box.hidden = false;
    box.innerHTML =
      row(ICON_SETTING, config.labels.setting || 'Price', config.ring.title || '', money(ring, cur)) +
      row(ICON_DIAMOND, config.labels.diamond || 'Stone price', shortTitle(d), money(d.price, d.currency),
        '<button type="button" class="ddv-summary__change" data-ddv-summary-change>' + esc(config.labels.change || 'Change diamond') + '</button>') +
      (total ? '<div class="ddv-summary__total"><span class="ddv-summary__total-label">' + esc(config.labels.total || 'Final price') + '</span>' +
        '<span class="ddv-summary__total-price">' + esc(total) + '</span></div>' : '');
  }

  // Keep the chosen stone in the URL (?diamond=ID) so a reload returns to the same step. The diamond app
  // reads the same parameter and restores its own selection from it.
  var urlRestorePending = false;
  // The chosen stone is also kept in this tab's session, so a reload (or Back) restores it at once.
  var NAV_TYPE = (performance.getEntriesByType && (performance.getEntriesByType('navigation')[0] || {}).type) || '';
  var STORE_KEY = 'cb-ddv:' + location.pathname;
  var savedStone = null; // read before the first render, which would otherwise clear it
  try { savedStone = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) {}
  function saveConfirmed() {
    try {
      if (state.confirmed) sessionStorage.setItem(STORE_KEY, JSON.stringify(state.confirmed));
      else sessionStorage.removeItem(STORE_KEY);
    } catch (e) {}
  }
  function syncDiamondParam() {
    if (urlRestorePending) return;
    saveConfirmed();
    try {
      var u = new URL(location.href);
      var has = u.searchParams.get('diamond');
      var want = state.confirmed && state.confirmed.id ? String(state.confirmed.id) : '';
      if (want && has !== want) { u.searchParams.set('diamond', want); history.replaceState(history.state, '', u.toString()); }
      else if (!want && has) { u.searchParams.delete('diamond'); history.replaceState(history.state, '', u.toString()); }
    } catch (e) {}
  }

  function renderPrices() {
    renderPageBar();
    renderComplete();
    syncDiamondParam();
    var d = state.diamond;
    var ring = ringPrice();
    var cur = activeCurrency();
    setText('ringPrice', money(ring, cur));
    var v = ringVariant();
    var img = (v && v.image) || config.ring.image;
    setHTML('ringThumb', img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '');
    if (!d) { setText('price', ''); setText('total', ''); return; }
    setText('price', money(d.price, d.currency));
    // Only add up when both prices are in the same currency.
    setText('total', d.price != null && ring != null && d.currency === cur ? money(ring + d.price, cur) : '');
  }

  function renderSpecs(d) {
    var grid = root.querySelector('[data-ddv-specgrid]');
    if (!grid) return;
    var lw = d.length && d.width ? d.length + ' × ' + d.width : '';
    var cells = [
      [d.carat != null ? d.carat.toFixed(2) : '', 'Carat'], [d.color, 'Color'], [d.clarity, 'Clarity'],
      [[d.shape, d.cut].filter(has).join(' · '), 'Cut'], [d.ratio, 'Ratio'], [lw, 'L/W (mm)']
    ].filter(function (c) { return has(c[0]); });
    grid.innerHTML = cells.map(function (c) { return '<div><dd>' + esc(c[0]) + '</dd><dt>' + esc(c[1]) + '</dt></div>'; }).join('');
    grid.hidden = !cells.length;
  }

  function renderDetails(d) {
    var list = root.querySelector('[data-ddv-details]');
    if (!list) return;
    var rows = [
      ['Shape', d.shape], ['Size', d.carat != null ? d.carat.toFixed(2) + ' ct' : ''], ['Color', d.color],
      ['Clarity', d.clarity], ['Cut', d.cut], ['L/W (mm)', d.length && d.width ? d.length + ' × ' + d.width : ''],
      ['Ratio', d.ratio], ['Table', has(d.table) ? d.table + '%' : ''], ['Depth', has(d.depth) ? d.depth + '%' : ''],
      ['Fluorescence', d.fluor], ['Polish', d.polish], ['Symmetry', d.symmetry]
    ].filter(function (r) { return has(r[1]); });
    list.innerHTML = rows.map(function (r) { return '<div><dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd></div>'; }).join('');
  }

  function renderCert(d) {
    root.querySelectorAll('[data-ddv-cert-link]').forEach(function (a) {
      var url = d.certUrl || (has(d.reportNo) ? (a.dataset.template || '').replace('[number]', encodeURIComponent(d.reportNo)) : '');
      a.hidden = !url;
      if (url) a.href = url;
    });
    setText('lab', d.lab || 'IGI');
    setText('reportNo', has(d.reportNo) ? (d.lab ? d.lab + ' ' : '') + 'Report ' + d.reportNo : '');
  }

  function outlinePath(shape, w, h) {
    var s = String(shape).toLowerCase();
    var x = 100 - w / 2, y = 70 - h / 2;
    if (/marquise/.test(s)) return 'M100 ' + y + ' Q' + (x + w * 1.05) + ' 70 100 ' + (y + h) + ' Q' + (x - w * 0.05) + ' 70 100 ' + y + 'Z';
    if (/pear/.test(s)) return 'M100 ' + y + ' Q' + (x + w) + ' ' + (y + h * 0.55) + ' ' + (x + w) + ' ' + (y + h * 0.7) + ' A' + w / 2 + ' ' + h * 0.3 + ' 0 0 1 ' + x + ' ' + (y + h * 0.7) + ' Q' + x + ' ' + (y + h * 0.55) + ' 100 ' + y + 'Z';
    if (/heart/.test(s)) return 'M100 ' + (y + h) + ' L' + x + ' ' + (y + h * 0.4) + ' A' + w / 4 + ' ' + h * 0.3 + ' 0 0 1 100 ' + (y + h * 0.2) + ' A' + w / 4 + ' ' + h * 0.3 + ' 0 0 1 ' + (x + w) + ' ' + (y + h * 0.4) + 'Z';
    if (/emerald|asscher|radiant|princess|baguette|square/.test(s)) {
      var c = /princess|baguette/.test(s) ? 0 : Math.min(w, h) * 0.15;
      return 'M' + (x + c) + ' ' + y + ' H' + (x + w - c) + ' L' + (x + w) + ' ' + (y + c) + ' V' + (y + h - c) + ' L' + (x + w - c) + ' ' + (y + h) + ' H' + (x + c) + ' L' + x + ' ' + (y + h - c) + ' V' + (y + c) + 'Z';
    }
    if (/cushion/.test(s)) return 'M' + x + ' ' + y + ' m' + w * 0.2 + ' 0 h' + w * 0.6 + ' q' + w * 0.2 + ' 0 ' + w * 0.2 + ' ' + h * 0.2 + ' v' + h * 0.6 + ' q0 ' + h * 0.2 + ' -' + w * 0.2 + ' ' + h * 0.2 + ' h-' + w * 0.6 + ' q-' + w * 0.2 + ' 0 -' + w * 0.2 + ' -' + h * 0.2 + ' v-' + h * 0.6 + ' q0 -' + h * 0.2 + ' ' + w * 0.2 + ' -' + h * 0.2 + 'Z';
    return 'M' + (100 - w / 2) + ' 70 A' + w / 2 + ' ' + h / 2 + ' 0 1 0 ' + (100 + w / 2) + ' 70 A' + w / 2 + ' ' + h / 2 + ' 0 1 0 ' + (100 - w / 2) + ' 70Z';
  }

  function renderDiagram(d) {
    var pane = root.querySelector('[data-ddv-diagram]');
    if (!pane) return;
    var hasDims = d.length && d.width;
    var hasProfile = has(d.table) || has(d.depth);
    if (!hasDims && !hasProfile) { pane.hidden = true; pane.innerHTML = ''; return; }
    pane.hidden = false;
    // Editor "Shape diagram" blocks first, then the built-in artwork for the shape.
    var shapeKey = String(d.shape || '').toLowerCase().replace(/\s+/g, ' ').trim();
    if (/cushion/.test(shapeKey)) shapeKey = /elongat|long/.test(shapeKey) ? 'elongated cushion' : 'cushion';
    else if (/emerald|asscher/.test(shapeKey)) shapeKey = 'emerald';
    else if (/round|brilliant/.test(shapeKey)) shapeKey = 'round';
    var custom = config.shapes[String(d.shape).toLowerCase().trim()] || (config.diagrams || {})[shapeKey] || {};
    // Shape artwork from the editor, with measurement labels placed next to its bars.
    if (custom.art) {
      var pos = custom.pos || {};
      function label(cls, x, y, name, value) {
        if (!has(value)) return '';
        return '<span class="ddv-dlabel ddv-dlabel--' + cls + '" style="--x:' + x + '%;--y:' + y + '%">' +
          '<strong>' + name + '</strong> ' + esc(value) + '</span>';
      }
      pane.hidden = false;
      pane.innerHTML = '<div class="ddv-diagram-art"><img src="' + esc(custom.art) + '" alt="' + esc(d.shape + ' diagram') + '" loading="lazy">' +
        label('top', pos.width_x, pos.width_y, 'Width', d.width ? d.width + ' mm' : '') +
        label('left', pos.length_x, pos.length_y, 'Length', d.length ? d.length + ' mm' : '') +
        label('top', pos.table_x, pos.table_y, 'Table', has(d.table) ? d.table + '%' : '') +
        label('right', pos.depth_x, pos.depth_y, 'Depth', has(d.depth) ? d.depth + '%' : '') +
        '</div>';
      return;
    }
    // Draw with length vertical and width horizontal, as on the certificate diagram.
    var L = d.length || 1, W = d.width || 1, k = 100 / Math.max(L, W);
    var w = Math.max(30, W * k), h = Math.max(30, L * k);
    var top = '<svg viewBox="0 0 200 140" role="img" aria-label="' + esc(['Width', d.width, 'mm, length', d.length, 'mm'].join(' ')) + '">' +
      (custom.top ? '<image href="' + esc(custom.top) + '" x="45" y="15" width="110" height="110" preserveAspectRatio="xMidYMid meet"/>' : '<path class="ddv-outline" d="' + outlinePath(d.shape, w, h) + '"/>') +
      (d.width ? '<line class="ddv-dim" x1="' + (100 - w / 2) + '" y1="12" x2="' + (100 + w / 2) + '" y2="12"/><text x="100" y="8" text-anchor="middle">WIDTH ' + esc(d.width) + ' mm</text>' : '') +
      (d.length ? '<line class="ddv-dim" x1="' + (100 - w / 2 - 10) + '" y1="' + (70 - h / 2) + '" x2="' + (100 - w / 2 - 10) + '" y2="' + (70 + h / 2) + '"/><text transform="translate(' + (100 - w / 2 - 15) + ' 70) rotate(-90)" text-anchor="middle">LENGTH ' + esc(d.length) + ' mm</text>' : '') +
      '</svg>';
    var side = hasProfile ? '<svg viewBox="0 0 200 100" role="img" aria-label="' + esc('Table ' + d.table + '%, depth ' + d.depth + '%') + '">' +
      (custom.side ? '<image href="' + esc(custom.side) + '" x="40" y="10" width="120" height="80" preserveAspectRatio="xMidYMid meet"/>' : '<path class="ddv-outline" d="M70 25 H130 L160 42 L100 92 L40 42 Z M40 42 H160"/>') +
      (has(d.table) ? '<line class="ddv-dim" x1="70" y1="16" x2="130" y2="16"/><text x="100" y="11" text-anchor="middle">TABLE ' + esc(d.table) + '%</text>' : '') +
      (has(d.depth) ? '<line class="ddv-dim" x1="168" y1="25" x2="168" y2="92"/><text transform="translate(180 58) rotate(-90)" text-anchor="middle">DEPTH ' + esc(d.depth) + '%</text>' : '') +
      '</svg>' : '';
    pane.innerHTML = '<div class="ddv-diagram">' + top + side + '</div>';
  }

  // Supplier 360° players draw a square video with their own controls/watermark at the
  // edges. Load each at its native square size and zoom slightly so only the video shows.
  //  - jaykar.co.in wraps the video page in a branded header: load the inner page (q=…).
  //  - v360 players fill the frame; zoom slider top-right, watermark bottom-right.
  var VIEWER_CROP = 1;
  function viewerSpec(url) {
    var m = /jaykar\.co\.in\/.*[?&]q=([^&]+)/.exec(url);
    // Jaykar: 450px stage centred at the top of the page, its buttons right below it.
    // Load it in a wider/taller frame and show only the stage, so the buttons stay hidden.
    if (m) return { src: decodeURIComponent(m[1]), size: 450, crop: 1, frame: 600, offX: 75, offY: 0 };
    return { src: url, size: 480, crop: VIEWER_CROP, frame: 480, offX: 0, offY: 0 };
  }
  function fitViewer(frame) {
    if (!frame) return;
    var size = Number(frame.dataset.size) || 480;
    var offX = Number(frame.dataset.offx) || 0, offY = Number(frame.dataset.offy) || 0;
    function fit() {
      var box = frame.parentElement;
      if (!box || !frame.isConnected) return;
      var w = box.clientWidth, h = box.clientHeight;
      var k = Math.max(w, h) * (Number(frame.dataset.crop) || VIEWER_CROP) / size;
      var x = (w - size * k) / 2 - offX * k, y = (h - size * k) / 2 - offY * k;
      frame.style.transform = 'translate(' + x + 'px,' + y + 'px) scale(' + k + ')';
    }
    fit();
    if (window.ResizeObserver) new ResizeObserver(fit).observe(frame.parentElement);
  }

  function isVideoFile(u) { return /\.(mp4|webm|mov)(\?|$)/i.test(u); }

  function renderMedia(d) {
    var pane = root.querySelector('[data-ddv-media]');
    var key = (d.video || '') + '|' + (d.images[0] || '');
    if (pane.dataset.mediaKey === key && pane.querySelector('.ddv-media__main')) return;
    pane.dataset.mediaKey = key;
    var slides = d.video ? [{ type: isVideoFile(d.video) ? 'video' : 'iframe', url: d.video }] : [];
    d.images.slice(0, 1).forEach(function (u) { slides.push({ type: 'img', url: u }); });
    if (!slides.length) {
      pane.innerHTML = '<svg viewBox="0 0 200 140" width="60%" aria-hidden="true"><path class="ddv-outline" style="fill:none;stroke:currentColor" d="' + outlinePath(d.shape, 70, 100) + '"/></svg>';
      return;
    }
    var idx = 0;
    function show(i) {
      idx = (i + slides.length) % slides.length;
      var s = slides[idx];
      var main = pane.querySelector('.ddv-media__main');
      var alt = esc(shortTitle(d));
      if (s.type === 'img') {
        main.innerHTML = '<img src="' + esc(s.url) + '" alt="' + alt + '" loading="lazy">';
        var im = main.querySelector('img');
        im.addEventListener('error', function () {
          if (d.images[1] && im.src !== d.images[1]) { im.src = d.images[1]; return; }
          if (slides.length > 1) { slides.splice(idx, 1); build(); } else { main.innerHTML = ''; }
        });
      } else if (s.type === 'video') {
        main.innerHTML = '<video src="' + esc(s.url) + '" muted loop playsinline autoplay aria-label="' + alt + '"></video>';
      } else {
        var spec = viewerSpec(s.url);
        main.innerHTML = '<iframe class="ddv-viewer" src="' + esc(spec.src) + '" data-size="' + spec.size + '" data-crop="' + spec.crop + '" data-offx="' + spec.offX + '" data-offy="' + spec.offY + '" style="width:' + spec.frame + 'px;height:' + spec.frame + 'px" title="' + alt + ' 360°" scrolling="no" allow="autoplay; fullscreen"></iframe>';
        fitViewer(main.querySelector('iframe'));
      }
      pane.querySelectorAll('.ddv-media__thumbs button').forEach(function (b, j) { b.setAttribute('aria-current', j === idx ? 'true' : 'false'); });
      pane.classList.toggle('is-360', s.type !== 'img');
    }
    function build() {
      pane.classList.toggle('has-dots', slides.length > 1);
      pane.innerHTML = '<div class="ddv-media__main"></div>' + (slides.length > 1
        ? '<div class="ddv-media__thumbs">' + slides.map(function (s, i) {
          return '<button type="button" aria-label="' + (s.type === 'img' ? 'Photo' : '360° view') + ' ' + (i + 1) + '"></button>';
        }).join('') + '</div>' : '');
      pane.querySelectorAll('.ddv-media__thumbs button').forEach(function (b, i) { b.addEventListener('click', function () { show(i); }); });
      show(0);
    }
    build();
    // Swipe between slides on touch screens.
    var startX = null;
    pane.ontouchstart = function (e) { startX = e.touches[0].clientX; };
    pane.ontouchend = function (e) {
      if (startX == null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) show(idx + (dx < 0 ? 1 : -1));
      startX = null;
    };
  }

  function renderCarat(d) {
    var box = root.querySelector('[data-ddv-carat]');
    if (!box) return;
    var pills = Array.prototype.slice.call(box.querySelectorAll('.ddv-pill'));
    function pick(val) {
      pills.forEach(function (p) { p.setAttribute('aria-pressed', p.dataset.carat === val ? 'true' : 'false'); });
      box.querySelectorAll('.ddv-carat__img').forEach(function (im) { im.hidden = im.dataset.carat !== val; });
    }
    var best = pills[0];
    if (d.carat != null) {
      pills.forEach(function (p) { if (Math.abs(num(p.dataset.carat) - d.carat) < Math.abs(num(best.dataset.carat) - d.carat)) best = p; });
    }
    if (best) pick(best.dataset.carat);
    var cap = config.labels.shownWith || '';
    setText('caratCaption', d.carat != null && cap ? cap.replace('[carat]', Math.round(d.carat * 100) / 100) : '');
    pills.forEach(function (p) { p.onclick = function () { pick(p.dataset.carat); }; });
  }

  // Length / width / depth (mm) per shape and carat size, from assets/diamond-measurements.json.
  var measureData = null, measureReq = null;
  function loadMeasurements() {
    if (!measureReq) {
      measureReq = config.cgMeasureUrl
        ? fetch(config.cgMeasureUrl).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; })
        : Promise.resolve(null);
      measureReq.then(function (j) { measureData = j; });
    }
    return measureReq;
  }
  function measureFor(shape, carat) {
    if (!measureData) return null;
    var s = String(shape || '').toLowerCase();
    // Longest shape name first so "elongated cushion" wins over "cushion".
    var keys = Object.keys(measureData).filter(function (k) { return k.charAt(0) !== '_' && k !== 'sizes'; })
      .sort(function (a, b) { return b.length - a.length; });
    var key = keys.filter(function (k) { return s.indexOf(k) > -1; })[0];
    if (!key) return null;
    var i = (measureData.sizes || []).indexOf(Number(carat).toFixed(2));
    var row = i > -1 && measureData[key][i];
    return row ? { length: row[0], width: row[1], depth: row[2] } : null;
  }

  // Layered size guide: hand photo + stone image scaled by carat (diameter ∝ ∛carat).
  function renderCaratGuide(d) {
    var box = root.querySelector('[data-ddv-cg]');
    if (!box) return;
    var sizes = String(box.dataset.sizes || '').split(',').map(num).filter(function (n) { return n > 0; });
    if (!sizes.length) return;
    // Common settings, overridden per shape by its "Size guide diamond" block.
    var stones = config.cgStones || {};
    var own = stones[String(d.shape || '').toLowerCase().trim()] || {};
    if (typeof own === 'string') own = { img: own };
    var minW = num(own.min) || num(box.dataset.min) || 8, maxW = num(own.max) || num(box.dataset.max) || 16;
    // Keep the section's values on the element once, then apply this shape's overrides.
    ['x', 'y', 'rot'].forEach(function (k) {
      var prop = '--cg-' + k, key = 'cgBase' + k;
      if (box.dataset[key] == null) box.dataset[key] = box.style.getPropertyValue(prop);
      var v = own[k];
      box.style.setProperty(prop, v != null && v !== '' ? v + (k === 'rot' ? 'deg' : '%') : box.dataset[key]);
    });
    var lo = Math.min.apply(null, sizes), hi = Math.max.apply(null, sizes);
    var stone = box.querySelector('[data-ddv-cg-stone]');
    // Diamond image for this shape (falls back to the default image).
    var src = own.img || config.cgDefaultStone;
    if (stone) {
      stone.hidden = !src;
      if (src && stone.getAttribute('src') !== src) stone.setAttribute('src', src);
    }
    var bar = box.querySelector('[data-ddv-cg-bar]');
    var cap = box.querySelector('[data-ddv-cg-caption]');
    var closest = sizes[0];
    var curCt = closest;
    if (d.carat != null) sizes.forEach(function (c) { if (Math.abs(c - d.carat) < Math.abs(closest - d.carat)) closest = c; });
    bar.innerHTML = sizes.map(function (c) {
      return '<button type="button" role="radio" class="ddv-cg__opt" data-ct="' + c + '">' + c.toFixed(1) + '</button>';
    }).join('');
    function pick(c) {
      bar.querySelectorAll('.ddv-cg__opt').forEach(function (b) {
        var on = num(b.dataset.ct) === c;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on);
      });
      // Smallest carat = min zoom, largest = max zoom; in between follows diameter (∛carat).
      var t = hi > lo ? (Math.cbrt(c) - Math.cbrt(lo)) / (Math.cbrt(hi) - Math.cbrt(lo)) : 0;
      if (stone) stone.style.width = (minW + (maxW - minW) * t).toFixed(2) + '%';
      // "closest to your carat size 3.05ct": the stone's real carat (2 decimals, trailing 0 trimmed).
      var closestText = config.labels.closest || '*closest to your carat size [carat]ct';
      var note = d.carat != null && c === closest
        ? ' <span>' + esc(closestText.replace('[carat]', (Math.round(d.carat * 100) / 100).toFixed(2).replace(/0$/, ''))) + '</span>' : '';
      cap.innerHTML = 'Shown with <strong>' + c.toFixed(1) + 'ct</strong>' + note;
      curCt = c;
      showMeasurements(c);
    }
    var meas = box.querySelector('[data-ddv-cg-meas]');
    function showMeasurements(c) {
      if (!meas) return;
      var m = measureFor(d.shape, c);
      if (!m) { meas.hidden = true; meas.innerHTML = ''; return; } // shapes without data (e.g. heart, asscher yet)
      meas.innerHTML = ['length', 'width', 'depth'].map(function (k) {
        return '<span><em>' + k + '</em> ' + m[k].toFixed(2) + ' mm</span>';
      }).join('');
      meas.hidden = false;
    }
    loadMeasurements().then(function () { showMeasurements(curCt); });
    bar.onclick = function (e) {
      var b = e.target instanceof Element && e.target.closest('.ddv-cg__opt');
      if (b) pick(num(b.dataset.ct));
    };
    pick(closest);
  }

  function gradeText(kind, grade) {
    var key = kind.toUpperCase() + ':' + String(grade).toUpperCase();
    var custom = config.grades[key];
    var def = DEFAULT_GRADES[key] || ['', ''];
    return { short: (custom && custom.short) || def[0], long: (custom && custom.long) || def[1] };
  }

  function renderQualities(d) {
    root.querySelectorAll('[data-ddv-quality]').forEach(function (card) {
      var kind = card.dataset.ddvQuality;
      var scale = card.dataset.scale.split(',');
      var stone = String(d[kind] || '').trim();
      var match = scale.filter(function (g) { return g.toUpperCase() === stone.toUpperCase(); })[0];
      card.hidden = !stone; // e.g. many lab stones have no cut grade
      card.querySelector('[data-ddv-qgrade]').textContent = stone;
      var wrap = card.querySelector('.ddv-scale');
      wrap.innerHTML = scale.map(function (g) {
        return '<button type="button" data-grade="' + esc(g) + '" class="' + (g === match ? 'is-stone' : '') + '">' + esc(g) + '</button>';
      }).join('');
      function pick(g) {
        wrap.querySelectorAll('button').forEach(function (b) { b.setAttribute('aria-pressed', b.dataset.grade === g ? 'true' : 'false'); });
        var t = gradeText(kind, g);
        card.querySelector('[data-ddv-qshort]').textContent = t.short;
        card.querySelector('[data-ddv-qlong]').textContent = t.long;
      }
      wrap.querySelectorAll('button').forEach(function (b) { b.addEventListener('click', function () { pick(b.dataset.grade); }); });
      pick(match || scale[scale.length - 1]);
    });
  }

  // Show the product page's own Consultation card here, so its design, text and
  // links are edited in one place. Falls back to this section's own card.
  function mirrorConsultCard() {
    var slot = root.querySelector('[data-ddv-consult-slot]');
    if (!slot || slot.dataset.source !== 'product' || slot.dataset.mirrored) return;
    var source = Array.prototype.slice.call(document.querySelectorAll('.consult-card-wrap'))
      .filter(function (el) { return !root.contains(el); })[0];
    if (!source) return;
    var copy = source.cloneNode(true);
    copy.removeAttribute('data-shopify-editor-block');
    copy.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
    slot.innerHTML = '';
    slot.appendChild(copy);
    slot.dataset.mirrored = '1';
  }

  // Phones: dots under the swipeable gallery show there is more to see.
  // Phones: soft prev/next chevrons over the swipeable gallery (no dots).
  function galleryDots() {
    var g = root.querySelector('.ddv-gallery');
    if (!g) return;
    var wrap = g.parentElement.classList.contains('ddv-gwrap') ? g.parentElement : null;
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'ddv-gwrap';
      g.parentNode.insertBefore(wrap, g);
      wrap.appendChild(g);
      wrap.insertAdjacentHTML('beforeend',
        '<button type="button" class="ddv-gnav ddv-gnav--prev" aria-label="Previous"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '<button type="button" class="ddv-gnav ddv-gnav--next" aria-label="Next"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>');
    }
    var prev = wrap.querySelector('.ddv-gnav--prev'), next = wrap.querySelector('.ddv-gnav--next');
    function panes() {
      return Array.prototype.slice.call(g.children).filter(function (p) { return p.classList.contains('ddv-pane') && !p.hidden; })
        .sort(function (a, b) { return a.offsetLeft - b.offsetLeft; });
    }
    function current(list) {
      var mid = g.scrollLeft + g.clientWidth / 2, best = 0;
      list.forEach(function (p, i) { if (Math.abs(p.offsetLeft + p.offsetWidth / 2 - mid) < Math.abs(list[best].offsetLeft + list[best].offsetWidth / 2 - mid)) best = i; });
      return best;
    }
    function go(step) {
      var list = panes(), i = Math.max(0, Math.min(list.length - 1, current(list) + step));
      g.scrollTo({ left: list[i].offsetLeft - (g.clientWidth - list[i].offsetWidth) / 2, behavior: 'smooth' });
    }
    function mark() {
      var list = panes(), i = current(list);
      prev.hidden = i <= 0;
      next.hidden = i >= list.length - 1;
    }
    prev.onclick = function () { go(-1); };
    next.onclick = function () { go(1); };
    g.onscroll = mark;
    mark();
  }

  function render(d) {
    setText('title', title(d));
    setText('shortTitle', shortTitle(d));
    setText('breadcrumb', [d.carat != null ? d.carat.toFixed(2) + ' ct' : '', d.shape, d.isLab ? 'Lab Diamond' : 'Diamond'].filter(Boolean).join(' '));
    setText('origin', d.isLab ? config.labels.lab : config.labels.natural);
    setHTML('step1Thumb', d.images[0] ? '<img src="' + esc(d.images[0]) + '" alt="" loading="lazy">' : '');
    renderPrices();
    renderSpecs(d);
    renderDiagram(d);
    renderMedia(d);
    renderCarat(d);
    renderCaratGuide(d);
    renderCert(d);
    renderDetails(d);
    renderQualities(d);
    galleryDots();
  }

  function resetView() {
    ['title', 'shortTitle', 'price', 'total', 'origin', 'breadcrumb', 'caratCaption'].forEach(function (n) { setText(n, ''); });
    setHTML('step1Thumb', '');
    var media = root.querySelector('[data-ddv-media]');
    if (media) { media.innerHTML = '<div class="ddv-skeleton"></div>'; delete media.dataset.mediaKey; }
    var spec = root.querySelector('[data-ddv-specgrid]');
    if (spec) spec.innerHTML = '';
    root.querySelector('[data-ddv-status]').textContent = '';
  }

  /* ---------- open / close ---------- */
  // The detail view takes the place of the product information section while open;
  // the header, step bar, footer and other sections stay on the page.
  function productSection() {
    // The product information section: found via its cart form, the app widget, or its id.
    var anchor = mainCartForm() || document.querySelector('#diamond-selector-widget') ||
      document.querySelector('.shopify-section[id$="__main"]');
    var section = anchor && (anchor.classList.contains('shopify-section') ? anchor : anchor.closest('.shopify-section'));
    return section && !section.contains(root) ? section : null;
  }
  // Open at the very top. The supplier's 360° player can make the browser scroll the
  // page while it loads (seconds later, smoothed by the theme), so for a few seconds any
  // scroll the shopper didn't make is undone.
  var GUARD_MS = 8000;
  // The theme scrolls the window on phones but an inner wrapper (.page-wrapper) on
  // desktop, so always work with whichever element actually scrolls.
  function scroller() {
    var el = root.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      var oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }
  function getScroll() { return scroller().scrollTop; }
  function setScroll(top) {
    var el = scroller();
    var prev = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    el.scrollTop = top;
    if (el === document.scrollingElement || el === document.documentElement) window.scrollTo(0, top);
    el.style.scrollBehavior = prev;
  }
  function jumpToTop() {
    var html = document.documentElement;
    state.guardUntil = Date.now() + GUARD_MS;
    state.prevScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    setScroll(0);
    setTimeout(function () { html.style.scrollBehavior = state.prevScrollBehavior || ''; }, GUARD_MS);
  }
  // Capture phase also sees scrolls of inner wrappers, not just the window.
  on(document, 'scroll', function () {
    if (!state.open || state.userScrolled || Date.now() > (state.guardUntil || 0)) return;
    if (getScroll() > 0) setScroll(0);
  }, { passive: true, capture: true });
  // The shopper scrolling themselves ends the guard.
  ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(function (ev) {
    on(window, ev, function (e) {
      if (!state.open) return;
      if (ev === 'mousedown' && e.target instanceof Element && e.target.closest('iframe')) return;
      state.userScrolled = true;
      document.documentElement.style.scrollBehavior = state.prevScrollBehavior || '';
    }, { passive: true });
  });

  // Template sections other than the step bar and this view (header/footer groups stay).
  function otherSections() {
    var own = root.closest('.shopify-section');
    var bar = pagebar && pagebar.closest('.shopify-section');
    return Array.prototype.slice.call(document.querySelectorAll('.shopify-section[id^="shopify-section-template--"]'))
      .filter(function (sec) { return sec !== own && sec !== bar && sec !== productSection(); });
  }

  function scrollToView(el) {
    if (!el) return;
    var header = document.querySelector('header-component, .header, #header-group');
    var offset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    var box = scroller();
    var boxTop = box === document.scrollingElement || box === document.documentElement ? 0 : box.getBoundingClientRect().top;
    var top = el.getBoundingClientRect().top - boxTop + getScroll() - offset - 8;
    setScroll(Math.max(0, top));
  }

  function open(id, mode, originBtn) {
    state.open = true;
    state.mode = mode;
    state.id = String(id);
    state.originBtn = originBtn || null;
    state.diamond = null;
    state.lastFocus = document.activeElement;
    var req = ++state.req;

    resetView();
    mirrorConsultCard();
    renderPrices();
    state.returnY = getScroll();
    if (mode !== 'preview') pushStep('detail');
    state.userScrolled = false;
    var main = productSection();
    if (main) main.hidden = true;
    otherSections().forEach(function (sec) { if (!sec.hidden) { sec.hidden = true; sec.dataset.ddvHid = '1'; } });
    overlay.hidden = false;
    overlay.classList.add('is-open');
    // Start at the top of the page (header + step bar + diamond view), instantly.
    jumpToTop();
    var title = overlay.querySelector('.ddv-title');
    if (title) { title.setAttribute('tabindex', '-1'); title.focus({ preventScroll: true }); }

    // Show what the app already displays right away (incl. its 360 video), then fill in
    // the full record; never drop a video the app had because the API record lacks it.
    var quick = scrapeFromButton(originBtn, id);
    if (quick.carat || quick.videoUrl || quick.imageUrl) {
      state.diamond = normalize(quick);
      render(state.diamond);
    }
    fetchDiamond(id).then(function (raw) {
      if (req !== state.req) return;
      if (!raw.videoUrl && quick.videoUrl) raw.videoUrl = quick.videoUrl;
      if (!raw.imageUrl && !raw.image && quick.imageUrl) raw.imageUrl = quick.imageUrl;
      state.diamond = normalize(raw);
      render(state.diamond);
    }).catch(function () {
      if (req !== state.req) return;
      state.diamond = normalize(quick);
      render(state.diamond);
    });
  }

  function close() {
    if (!state.open) return;
    state.open = false;
    state.req++;
    overlay.classList.remove('is-open');
    overlay.hidden = true;
    // Opened from the app's popup: go back to the diamond list, not the popup.
    if (document.documentElement.classList.contains('ddv-hide-app-modal') && !state.keepPopup) {
      var popClose = document.querySelector('#ds-modal-close-btn, #ds-modal-back-btn');
      if (popClose) popClose.click();
      document.documentElement.classList.remove('ddv-hide-app-modal');
    }
    var main = productSection();
    if (main) main.hidden = false;
    document.querySelectorAll('[data-ddv-hid]').forEach(function (sec) { sec.hidden = false; delete sec.dataset.ddvHid; });
    renderPrices();
    if (state.returnY != null) setScroll(state.returnY);
    else scrollToView(document.querySelector('#diamond-selector-widget') || pagebar);
    var media = root.querySelector('[data-ddv-media]');
    if (media) { media.innerHTML = '<div class="ddv-skeleton"></div>'; delete media.dataset.mediaKey; } // stops video/iframe
  }

  function restoreFocus(el) {
    var target = el && el.isConnected ? el : state.lastFocus;
    if (target && target.isConnected && target.focus) target.focus();
  }

  // Let the app do its normal selection for this stone.
  function passToApp(id) {
    var btn = state.originBtn && state.originBtn.isConnected ? state.originBtn
      : document.querySelector(APP_SELECT_BTN + '[data-action-select="' + CSS.escape(id) + '"]');
    if (!btn) return false;
    state.bypass = true;
    try { btn.click(); } finally { state.bypass = false; }
    return true;
  }

  function finish() {
    var id = state.id;
    var mode = state.mode;
    // Finishing from the popup: keep it (hidden) so the app can take the selection.
    state.keepPopup = mode === 'select';
    close();
    state.keepPopup = false;
    if (mode === 'select') {
      var fromPopup = document.querySelector('#ds-modal-root-wrapper [data-action-select]');
      if (fromPopup) document.documentElement.classList.add('ddv-hide-app-modal'); // the app closes it after its check
      if (passToApp(id)) { state.confirmed = state.diamond; renderPrices(); pushStep('final'); }
      if (fromPopup) setTimeout(function () { document.documentElement.classList.remove('ddv-hide-app-modal'); }, 4000);
      // Bring the shopper back to the Selected Diamond card once the app has drawn it.
      setTimeout(function () {
        var card = document.querySelector('.ds-selected-preview-card-wrap');
        if (card) card.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        restoreFocus(document.querySelector(APP_VIEW_BTN));
      }, 600);
    } else {
      pushStep('final');
      restoreFocus(document.querySelector(APP_VIEW_BTN));
    }
  }

  // "Change diamond" on the final step: same result as the stone's Change/Remove — back to the diamond list.
  function changeDiamond() {
    state.confirmed = null;
    renderPrices();
    var change = document.querySelector(APP_CHANGE_BTN);
    if (change) change.click();
    setTimeout(function () { restoreFocus(document.querySelector('#diamond-selector-widget')); }, 300);
  }
  on(document, 'click', function (e) {
    if (e.target instanceof Element && e.target.closest('[data-ddv-summary-change]')) { e.preventDefault(); changeDiamond(); }
  });

  function remove() {
    var mode = state.mode;
    var origin = state.originBtn;
    close();
    if (mode === 'view') {
      state.confirmed = null;
      renderPrices();
      // Stone was already selected: let the app clear it and reopen its list.
      var change = document.querySelector(APP_CHANGE_BTN);
      if (change) change.click();
      setTimeout(function () { restoreFocus(document.querySelector('#diamond-selector-widget')); }, 300);
    } else {
      restoreFocus(origin);
    }
  }

  /* ---------- ring size first ---------- */
  // A ring size must be chosen before the shopper can choose a diamond.
  var SIZE_GATED = '#ds-open-selector-btn, [data-ddv-choose], ' + APP_SELECT_BTN;
  function ringSizeMissing() {
    var picker = document.querySelector('.shopify-section[id$="__main"] .rsp, .rsp');
    if (!picker) return null;
    var prop = picker.querySelector('[data-rsp-property]');
    return prop && !prop.value ? picker : null;
  }
  function askForRingSize(picker) {
    var error = picker.querySelector('[data-rsp-error]');
    if (error) error.hidden = false;
    picker.classList.add('ddv-rsp-needed');
    if (state.open) close();
    var box = scroller();
    var boxTop = box === document.scrollingElement || box === document.documentElement ? 0 : box.getBoundingClientRect().top;
    setScroll(Math.max(0, picker.getBoundingClientRect().top - boxTop + getScroll() - 140));
    var trigger = picker.querySelector('[data-rsp-trigger], button');
    if (trigger) trigger.focus({ preventScroll: true });
  }
  on(document, 'change', function () {
    var picker = document.querySelector('.rsp.ddv-rsp-needed');
    if (picker && !ringSizeMissing()) picker.classList.remove('ddv-rsp-needed');
  });
  on(document, 'click', function () {
    setTimeout(function () {
      var picker = document.querySelector('.rsp.ddv-rsp-needed');
      if (picker && !ringSizeMissing()) picker.classList.remove('ddv-rsp-needed');
    }, 50);
  });

  /* ---------- events ---------- */
  // Capture phase on window runs before the app's own button listeners.
  on(window, 'click', function (e) {
    if (state.bypass || !(e.target instanceof Element)) return;
    if (e.target.closest(SIZE_GATED)) {
      var picker = ringSizeMissing();
      if (picker) {
        e.preventDefault();
        e.stopImmediatePropagation();
        askForRingSize(picker);
        return;
      }
    }
    var sel = e.target.closest(APP_SELECT_BTN);
    if (sel && sel.closest(APP_AREA) && !sel.classList.contains('selected')) {
      var id = sel.getAttribute('data-action-select');
      if (!id) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open(id, 'select', sel);
      // From the app's popup: hide the popup while our page shows. It stays in the page so
      // "Finish Your Setting" can still pass the selection to the app (which then closes it).
      if (sel.closest('#ds-modal-root-wrapper')) document.documentElement.classList.add('ddv-hide-app-modal');
      return;
    }
    // App's own "Change": the stone is no longer chosen.
    if (e.target.closest(APP_CHANGE_BTN) && state.confirmed) {
      state.confirmed = null;
      setTimeout(renderPrices, 0);
      return;
    }
    var view = e.target.closest(APP_VIEW_BTN);
    if (view && view.getAttribute('data-open-modal')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      open(view.getAttribute('data-open-modal'), 'view', view);
    }
  }, true);

  overlay.addEventListener('click', function (e) {
    var t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    // Close / Change Diamond / Remove from a stone being chosen = one step back in history.
    if (t.closest('[data-ddv-close], [data-ddv-change], [data-ddv-remove]') && state.mode === 'select' && history.state && history.state.ddv === 'detail') {
      history.back();
      return;
    }
    if (t.closest('[data-ddv-close]')) { close(); restoreFocus(state.originBtn); return; }
    if (t.closest('[data-ddv-finish]')) { finish(); return; }
    if (t.closest('[data-ddv-change]')) { close(); restoreFocus(state.originBtn); return; }
    if (t.closest('[data-ddv-remove]')) { remove(); return; }
  });

  on(document, 'keydown', function (e) {
    if (!state.open) return;
    if (e.key === 'Escape' && root.dataset.closeOnEsc === 'true') {
      e.preventDefault();
      close();
      restoreFocus(state.originBtn);
      return;
    }
  });

  // Ring variant changes (metal, size…): update step 2 and the total.
  function onVariantChange() { setTimeout(renderPrices, 50); }
  on(document, 'click', function (e) {
    if (e.target instanceof Element && e.target.closest('.rsp')) setTimeout(renderComplete, 50);
  });
  on(document, 'change', onVariantChange);
  on(document, 'variant:update', onVariantChange);

  if (pagebar) {
    on(pagebar, 'click', function (e) {
      var t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t.closest('[data-ddv-choose]')) {
        var openBtn = document.querySelector('#ds-open-selector-btn');
        var widget = document.querySelector('#diamond-selector-widget');
        if (openBtn) openBtn.click();
        if (widget) widget.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      } else if (t.closest('[data-ddv-page-remove]') && state.open) {
        remove();
      } else if (t.closest('[data-ddv-page-remove]')) {
        var change = document.querySelector(APP_CHANGE_BTN);
        state.confirmed = null;
        renderPrices();
        if (change) change.click();
      }
    });
  }
  /* ---------- browser Back retraces the steps ---------- */
  // product → list → detail → final ring. Each step adds a history entry (same URL),
  // so Back goes final → detail → list → product.
  var restoring = false;
  function pushStep(step) {
    if (restoring) return;
    var cur = history.state && history.state.ddv;
    if (cur === step && step !== 'detail') return;
    history.pushState({ ddv: step, id: state.id }, '');
  }
  on(window, 'popstate', function (e) {
    var step = e.state && e.state.ddv;
    restoring = true;
    try {
      if (step === 'detail') {
        // Back from the final ring page: show the chosen stone's details again.
        var id = (e.state && e.state.id) || (state.confirmed && state.confirmed.id);
        if (id) open(id, state.confirmed ? 'view' : 'select', null);
      } else if (step === 'list') {
        if (state.open) close();
        if (state.confirmed) {
          // Back from details of a confirmed stone: return to the diamond list.
          state.confirmed = null;
          renderPrices();
          var change = document.querySelector(APP_CHANGE_BTN);
          if (change) change.click();
        } else if (!document.querySelector('#diamond-selector-widget .ds-table-row')) {
          var openBtn = document.querySelector('#ds-open-selector-btn');
          if (openBtn) openBtn.click();
        }
      } else {
        // Back to the plain product page.
        if (state.open) close();
        var closeBtn = document.querySelector('#ds-close-selector-btn');
        if (closeBtn) closeBtn.click();
        setScroll(0);
      }
    } finally {
      restoring = false;
    }
  });
  // Opening the diamond list from the page.
  on(document, 'click', function (e) {
    if (e.target instanceof Element && e.target.closest('#ds-open-selector-btn, [data-ddv-choose]') && !ringSizeMissing()) pushStep('list');
  });

  // Phones: certificate + carat guide tiles sit above Diamond Details instead of in the swipe gallery.
  var mqPhone = window.matchMedia('(max-width: 749px)');
  function placeTiles() {
    var gallery = root.querySelector('.ddv-gallery');
    var info = root.querySelector('.ddv-info');
    if (!gallery || !info) return;
    var tiles = root.querySelectorAll('.ddv-pane--cert, .ddv-pane--carat');
    if (mqPhone.matches) {
      var anchor = info.querySelector('.ddv-acc--side');
      tiles.forEach(function (t) { t.classList.add('ddv-tile--stacked'); anchor ? info.insertBefore(t, anchor) : info.appendChild(t); });
    } else {
      tiles.forEach(function (t) { t.classList.remove('ddv-tile--stacked'); gallery.appendChild(t); });
    }
    galleryDots();
  }
  if (mqPhone.addEventListener) on(mqPhone, 'change', placeTiles);
  placeTiles();

  var viewSteps = root.querySelector('[data-ddv-steps="overlay"]');
  if (pagebar && viewSteps) viewSteps.hidden = true;
  // A ?diamond= in the address must survive the first render (which would otherwise remove it as "no stone chosen").
  var bootQs = new URLSearchParams(location.search);
  var bootParam = bootQs.get('ddv_ring') || bootQs.get('diamond');
  if (bootParam && /^[A-Za-z0-9_-]{1,64}$/.test(bootParam)) urlRestorePending = true;
  renderPrices();

  // From the cart's "View": ?ddv_ring=<diamond id> opens the finished ring ("Your Ring" step).
  // After a reload: ?diamond=ID puts the shopper back on the final step with that stone.
  var qs = new URLSearchParams(location.search);
  var ringParam = qs.get('ddv_ring') || qs.get('diamond');
  var isReload = NAV_TYPE === 'reload' || NAV_TYPE === 'back_forward';
  if (isReload && savedStone && savedStone.id && (!ringParam || String(savedStone.id) === String(ringParam))) {
    // Reload: straight back to the same step, same stone, same scroll position (nothing jumps to the top).
    state.id = String(savedStone.id);
    state.confirmed = state.diamond = savedStone;
    urlRestorePending = false;
    renderPrices();
  } else if (ringParam && /^[A-Za-z0-9_-]{1,64}$/.test(ringParam)) {
    urlRestorePending = true;
    fetchDiamond(ringParam).then(function (raw) {
      state.id = String(ringParam);
      state.confirmed = state.diamond = normalize(raw);
      urlRestorePending = false;
      renderPrices();
      pushStep('final');
      setScroll(0);
    }).catch(function () { urlRestorePending = false; });
  } else {
    urlRestorePending = false;
  }

  // Theme editor: show the overlay with a sample stone while the section is selected.
  if (window.Shopify && Shopify.designMode) {
    on(document, 'shopify:section:select', function (e) {
      if (!root.closest('#shopify-section-' + e.detail.sectionId) && root.id !== 'ddv-' + e.detail.sectionId) return;
      var base = (Shopify.routes && Shopify.routes.root) || '/';
      fetch(base + 'apps/diamonds?page=1&limit=1', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) { var d = (data.diamonds || data)[0]; if (d) open(d.id, 'preview', null); })
        .catch(function () {});
    });
    on(document, 'shopify:section:deselect', close);
  }

  return function () {
    bound.forEach(function (b) { b[0].removeEventListener(b[1], b[2], b[3]); });
    document.querySelectorAll('[data-ddv-hid]').forEach(function (sec) { sec.hidden = false; delete sec.dataset.ddvHid; });
    var main = productSection();
    if (main) main.hidden = false;
    document.documentElement.classList.remove('ddv-complete');
  };
  }
})();
